import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { motion } from "framer-motion";
import {
  Search,
  FileDown,
  Trash2,
  ArrowLeft,
  Phone,
  MapPin,
  Car,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function AdminCustomers() {
  const navigate = useNavigate();
  const receiptRef = useRef(null);


  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  /* ================= THEME SYNC ================= */
  useEffect(() => {
  const sync = () => {
    localStorage.getItem("theme");
  };
  sync();
  window.addEventListener("theme-change", sync);
  return () => window.removeEventListener("theme-change", sync);
}, []);


  /* ================= LOAD DATA ================= */
  useEffect(() => {
    const load = async () => {
      const q = query(
        collection(db, "services"),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q);
      setServices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    load();
  }, []);

  /* ================= FILTER ================= */
  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return services.filter(
      (x) =>
        x.customerName?.toLowerCase().includes(s) ||
        x.phone?.includes(s) ||
        x.location?.toLowerCase().includes(s)
    );
  }, [search, services]);

  /* ================= DELETE ================= */
  const remove = async (id) => {
    if (!window.confirm("Delete this record permanently?")) return;
    await deleteDoc(doc(db, "services", id));
    setServices((s) => s.filter((x) => x.id !== id));
  };

  /* ================= MARK PAID ================= */
  const markArrearsPaid = async (item) => {
    if (item.balance <= 0) return;
    if (!window.confirm("Mark outstanding arrears as PAID?")) return;

    await updateDoc(doc(db, "services", item.id), {
      amountPaid: item.amountCharged,
      balance: 0,
      paymentStatus: "paid",
      paidAt: new Date(),
    });

    setServices((prev) =>
      prev.map((x) =>
        x.id === item.id
          ? {
              ...x,
              amountPaid: x.amountCharged,
              balance: 0,
              paymentStatus: "paid",
            }
          : x
      )
    );
  };

  /* ================= DOWNLOAD PDF ================= */
  const downloadPDF = async (item) => {
    setSelected(item);

    setTimeout(async () => {
      const element = receiptRef.current;
      if (!element) return;

      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: "#d8d4d4",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = 210;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Receipt-${item.customerName || item.name || "Customer"}.pdf`);
    }, 300);
  };

  /* ================= THEME ================= */
  const theme = {
    page: "bg-[#0b0b0b] text-white",
    card: "bg-[#0f1115] border border-white/10",
    input:
      "bg-[#151820] border-white/20 text-white placeholder-gray-400",
    muted: "text-gray-400",
  };

  return (
    <>
      <div className={`min-h-screen p-6 ${theme.page}`}>
        {/* HEADER */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 text-sm font-semibold ${theme.muted}`}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <h1 className="text-3xl font-bold">Customers & Services</h1>
        </div>

        {/* SEARCH */}
        <div className="max-w-md mb-6 relative">
          <Search className="absolute left-3 top-3 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone or location"
            className={`w-full pl-10 pr-3 py-3 rounded-xl border ${theme.input}`}
          />
        </div>

        {/* TABLE */}
        <div className={`rounded-2xl shadow border overflow-x-auto ${theme.card}`}>
          <table className="w-full text-sm">
            <thead className="bg-black text-white">
              <tr>
                <th className="p-3 text-left">Customer</th>
                <th>Route</th>
                <th>Event Date</th>
                <th>Payment</th>
                <th>Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((item) => {
                const balance = item.balance ?? 0;

                return (
                  <motion.tr
                    key={item.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-white/5 hover:bg-white/5"
                  >
                    {/* CUSTOMER */}
                    <td className="p-3">
                     <p className="font-semibold text-white">
  {item.customerName || item.name || "Unnamed Customer"}
</p>

                      <p className={`text-xs flex gap-1 items-center ${theme.muted}`}>
                        <Phone size={12} /> {item.phone}
                      </p>
                      <p className={`text-xs flex gap-1 items-center ${theme.muted}`}>
                        <MapPin size={12} /> {item.location}
                      </p>
                    </td>

                    {/* ROUTE */}
                    <td className="text-xs">
                      <Car size={12} /> {item.route}
                    </td>

                    {/* EVENT DATE */}
                    <td className="text-xs">
                      {item.eventStart && item.eventEnd
                        ? `${item.eventStart} → ${item.eventEnd}`
                        : "—"}
                    </td>

                    {/* PAYMENT */}
                    <td>
                      <p>Charged: GHS {item.amountCharged}</p>
                      <p>Paid: GHS {item.amountPaid}</p>
                      <p className={balance > 0 ? "text-red-400" : "text-green-400"}>
                        Balance: GHS {balance}
                      </p>
                    </td>

                    {/* STATUS */}
                    <td>
                      {balance === 0 ? (
                        <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-300">
                          Paid
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded bg-yellow-500/20 text-yellow-300">
                          Partial
                        </span>
                      )}
                    </td>

                    {/* ACTIONS */}
                    <td className="p-3 text-right space-x-2">
                      <button
                        onClick={() => downloadPDF(item)}
                        className="px-3 py-1 bg-indigo-600 text-white text-xs rounded"
                      >
                        <FileDown size={14} /> PDF
                      </button>

                      <button
                        onClick={() => markArrearsPaid(item)}
                        className="px-3 py-1 bg-emerald-600 text-white text-xs rounded"
                      >
                        Mark Paid
                      </button>

                      <button
                        onClick={() => remove(item.id)}
                        className="px-3 py-1 bg-red-600 text-white text-xs rounded"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= PDF RECEIPT ================= */}
{selected && (
  <div className="fixed left-[-9999px] top-0">
    <div
      ref={receiptRef}
      className="bg-white text-black"
      style={{
        width: "210mm",
        minHeight: "297mm",
        padding: "20mm",
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: "14px",
      }}
    >
      {/* HEADER */}
      <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
        <img src={logo} alt="Logo" style={{ height: "70px" }} />
        <div>
          <h2 style={{ margin: 0 }}>DEONYAK HEARSE SERVICE</h2>
          <p>0245226992 / 0244378871</p>
          <p>Bakaekyir – Near Goil Filling Station</p>
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />

      {/* CUSTOMER PARTICULARS */}
      <h3 style={{ marginBottom: "10px" }}>CUSTOMER PARTICULARS</h3>

      <p>
  <b>Customer Name:</b>{" "}
  {selected.customerName || selected.name || "Unnamed Customer"}
</p>

      <p><b>Phone Number:</b> {selected.phone}</p>
      <p><b>Location:</b> {selected.location}</p>

      <hr style={{ margin: "20px 0" }} />

      {/* SERVICE DETAILS */}
      <h3 style={{ marginBottom: "10px" }}>SERVICE DETAILS</h3>

      <p><b>Route:</b> {selected.route}</p>
      <p>
        <b>Event Date:</b>{" "}
        {selected.eventStart && selected.eventEnd
          ? `${selected.eventStart} → ${selected.eventEnd}`
          : "—"}
      </p>

      <hr style={{ margin: "20px 0" }} />

      {/* PAYMENT DETAILS */}
      <h3 style={{ marginBottom: "10px" }}>PAYMENT DETAILS</h3>

      <p><b>Amount Charged:</b> GHS {selected.amountCharged}</p>
      <p><b>Amount Paid:</b> GHS {selected.amountPaid}</p>
      <p><b>Balance:</b> GHS {selected.balance}</p>

      {/* SIGNATURES */}
      <div
        style={{
          marginTop: "50px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          <p>__________________________</p>
          <p>Authorized Signature</p>
        </div>
        <div>
          <p>__________________________</p>
          <p>Customer Signature</p>
        </div>
      </div>

      <p
        style={{
          textAlign: "center",
          marginTop: "40px",
          fontWeight: "bold",
        }}
      >
        Thank you for choosing Deonyak Hearse Service
      </p>
    </div>
  </div>
)}
    </>  
    );
}
